import { useState } from "react";
import { iotConfig } from "@/mocks/settings";

export default function IoTConfig() {
  const [config, setConfig] = useState(iotConfig);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<null | "success" | "fail">(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [editIp, setEditIp] = useState("");
  const [showAddIp, setShowAddIp] = useState(false);

  const handleTest = () => {
    setTesting(true);
    setTestResult(null);
    setTimeout(() => {
      setTesting(false);
      setTestResult("success");
    }, 2000);
  };

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }, 1000);
  };

  const toggleProtocol = (name: string) => {
    setConfig(prev => ({
      ...prev,
      protocols: prev.protocols.map(p =>
        p.name === name ? { ...p, enabled: !p.enabled } : p
      ),
    }));
  };

  const removeIp = (ip: string) => {
    setConfig(prev => ({
      ...prev,
      security: {
        ...prev.security,
        ipWhitelist: prev.security.ipWhitelist.filter(i => i !== ip),
      },
    }));
  };

  const addIp = () => {
    if (editIp.trim()) {
      setConfig(prev => ({
        ...prev,
        security: {
          ...prev.security,
          ipWhitelist: [...prev.security.ipWhitelist, editIp.trim()],
        },
      }));
      setEditIp("");
      setShowAddIp(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* 平台连接状态 */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-800">IoT平台连接状态</h3>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
            <span className="text-xs text-green-600 font-medium">{config.platform.status}</span>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4 mb-4">
          {[
            { label: "平台名称", value: config.platform.name, icon: "ri-cloud-line" },
            { label: "接入设备", value: `${config.platform.deviceCount} 台`, icon: "ri-cpu-line" },
            { label: "在线设备", value: `${config.platform.onlineCount} 台`, icon: "ri-wifi-line" },
            { label: "最后同步", value: config.platform.lastSync.split(" ")[1], icon: "ri-refresh-line" },
          ].map((item, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${item.icon} text-sm text-[#E8531A]`}></i>
                </div>
                <span className="text-xs text-gray-500">{item.label}</span>
              </div>
              <div className="text-sm font-semibold text-gray-800">{item.value}</div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleTest}
            disabled={testing}
            className="flex items-center gap-2 px-4 py-2 bg-[#E8531A]/10 text-[#E8531A] text-sm rounded-lg hover:bg-[#E8531A]/20 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`${testing ? "ri-loader-4-line animate-spin" : "ri-signal-wifi-line"} text-sm`}></i>
            </div>
            {testing ? "测试中..." : "测试连接"}
          </button>
          {testResult === "success" && (
            <span className="flex items-center gap-1 text-xs text-green-600">
              <i className="ri-checkbox-circle-line"></i> 连接正常，延迟 12ms
            </span>
          )}
          {testResult === "fail" && (
            <span className="flex items-center gap-1 text-xs text-red-500">
              <i className="ri-close-circle-line"></i> 连接失败，请检查配置
            </span>
          )}
        </div>
      </div>

      {/* 基础配置 */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-4">基础连接配置</h3>
        <div className="space-y-3">
          {[
            { label: "平台名称", key: "name", value: config.platform.name },
            { label: "API接口地址", key: "apiUrl", value: config.platform.apiUrl },
            { label: "WebSocket地址", key: "wsUrl", value: config.platform.wsUrl },
            { label: "应用ID (AppId)", key: "appId", value: config.platform.appId },
            { label: "所属区域", key: "region", value: config.platform.region },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-4">
              <label className="text-sm text-gray-500 w-40 flex-shrink-0">{item.label}</label>
              <input
                defaultValue={item.value}
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50 bg-gray-50 focus:bg-white transition-colors"
              />
            </div>
          ))}
          <div className="flex items-center gap-4">
            <label className="text-sm text-gray-500 w-40 flex-shrink-0">应用密钥 (AppSecret)</label>
            <div className="flex-1 flex items-center gap-2">
              <input
                type={showSecret ? "text" : "password"}
                defaultValue="gascloud_secret_key_2026"
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50 bg-gray-50 focus:bg-white transition-colors"
              />
              <button
                onClick={() => setShowSecret(!showSecret)}
                className="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <i className={`${showSecret ? "ri-eye-off-line" : "ri-eye-line"} text-sm text-gray-500`}></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 通信协议 */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-4">通信协议配置</h3>
        <div className="grid grid-cols-2 gap-3">
          {config.protocols.map((proto, i) => (
            <div key={i} className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${proto.enabled ? "border-[#E8531A]/30 bg-[#E8531A]/5" : "border-gray-200 bg-gray-50"}`}>
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${proto.enabled ? "bg-[#E8531A]/10" : "bg-gray-200"}`}>
                  <i className={`ri-wifi-line text-sm ${proto.enabled ? "text-[#E8531A]" : "text-gray-400"}`}></i>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-800">{proto.name}</div>
                  <div className="text-xs text-gray-400">端口 {proto.port} {proto.tls ? "· TLS加密" : ""}</div>
                </div>
              </div>
              <button
                onClick={() => toggleProtocol(proto.name)}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${proto.enabled ? "bg-[#E8531A]" : "bg-gray-300"}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${proto.enabled ? "translate-x-5" : "translate-x-0.5"}`}></span>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* 数据同步配置 */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-4">数据同步参数</h3>
        <div className="grid grid-cols-2 gap-4">
          {[
            { label: "数据刷新间隔 (秒)", key: "interval", value: config.dataSync.interval },
            { label: "失败重试次数", key: "retryCount", value: config.dataSync.retryCount },
            { label: "重试间隔 (秒)", key: "retryInterval", value: config.dataSync.retryInterval },
            { label: "批量处理大小", key: "batchSize", value: config.dataSync.batchSize },
            { label: "请求超时 (秒)", key: "timeout", value: config.dataSync.timeout },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3">
              <label className="text-sm text-gray-500 flex-1">{item.label}</label>
              <input
                type="number"
                defaultValue={item.value}
                className="w-24 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50 text-right"
              />
            </div>
          ))}
        </div>
      </div>

      {/* 安全配置 */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-4">安全配置</h3>
        <div className="grid grid-cols-2 gap-4 mb-4">
          {[
            { label: "TLS版本", value: config.security.tlsVersion, icon: "ri-shield-check-line", color: "text-green-600" },
            { label: "证书到期", value: config.security.certExpiry, icon: "ri-file-shield-2-line", color: "text-blue-600" },
            { label: "认证方式", value: config.security.authMode, icon: "ri-key-2-line", color: "text-[#E8531A]" },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className={`${item.icon} text-lg ${item.color}`}></i>
              </div>
              <div>
                <div className="text-xs text-gray-400">{item.label}</div>
                <div className="text-sm font-medium text-gray-800">{item.value}</div>
              </div>
            </div>
          ))}
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">IP白名单</span>
            <button
              onClick={() => setShowAddIp(true)}
              className="flex items-center gap-1 text-xs text-[#E8531A] hover:underline cursor-pointer"
            >
              <i className="ri-add-line"></i> 添加
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {config.security.ipWhitelist.map((ip, i) => (
              <div key={i} className="flex items-center gap-1.5 px-3 py-1 bg-gray-100 rounded-full text-xs text-gray-700">
                <i className="ri-shield-line text-gray-400"></i>
                {ip}
                <button onClick={() => removeIp(ip)} className="w-3 h-3 flex items-center justify-center hover:text-red-500 cursor-pointer">
                  <i className="ri-close-line text-xs"></i>
                </button>
              </div>
            ))}
            {showAddIp && (
              <div className="flex items-center gap-1">
                <input
                  value={editIp}
                  onChange={e => setEditIp(e.target.value)}
                  placeholder="如 192.168.1.0/24"
                  className="px-2 py-1 text-xs border border-gray-300 rounded-full focus:outline-none focus:border-[#E8531A]/50 w-36"
                  onKeyDown={e => e.key === "Enter" && addIp()}
                  autoFocus
                />
                <button onClick={addIp} className="px-2 py-1 bg-[#E8531A] text-white text-xs rounded-full cursor-pointer whitespace-nowrap">确认</button>
                <button onClick={() => setShowAddIp(false)} className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded-full cursor-pointer whitespace-nowrap">取消</button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 bg-[#E8531A] text-white text-sm rounded-lg hover:bg-[#d44a15] cursor-pointer whitespace-nowrap disabled:opacity-50 transition-colors"
        >
          {saving ? "保存中..." : "保存配置"}
        </button>
        {saved && (
          <span className="flex items-center gap-1 text-sm text-green-600">
            <i className="ri-checkbox-circle-line"></i> 配置已保存
          </span>
        )}
      </div>
    </div>
  );
}
